import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import ModalViewModel from 'infra/ModalViewModel'
import { AlertFactory } from 'alerts/Alert'
import { Bundle } from 'admin/bundle/bundle'
import { BundleStore } from 'admin/bundle/BundleStore'
import { RefreshBundles } from 'admin/bundle/BundleSearchViewModel'
import {
  formatBundleNames,
  pluralizeBundles,
  showBulkActionError,
  showBulkActionResult,
} from 'admin/bundle/BulkActionAlerts'
import { Id } from 'types/definitions'

export class BulkArchiveBundleViewModel extends ModalViewModel {
  @observable
  selectedBundles: Bundle[] = []

  constructor(
    private bundleStore: BundleStore,
    private newAlert: AlertFactory,
    private refreshBundles: RefreshBundles,
  ) {
    super()
    makeObservable(this)

    this.close$.subscribe(this.reset)
  }

  /**
   * Already-archived bundles archive to a harmless no-op, so they're filtered
   * out before sending; anything that slips through lands in
   * `alreadyArchivedIds`.
   */
  @computed
  get archivableBundles() {
    return this.selectedBundles.filter((bundle) => !bundle.archived)
  }

  @action
  open(bundles: Bundle[]) {
    this.reset()
    this.selectedBundles = bundles

    this.show()
  }

  @action.bound
  reset() {
    this.selectedBundles = []

    this.archive.reset()
  }

  archive = task.resolved(async () => {
    const bundles = this.archivableBundles

    if (bundles.length === 0) {
      return
    }

    try {
      const result = await this.bundleStore.bulkArchive(
        Id.assert(bundles[0].workspace.id),
        bundles.map((bundle) => Id.assert(bundle.Id)),
      )

      const skippedIds = new Set<Id>([
        ...result.notFoundIds,
        ...result.alreadyArchivedIds,
      ])

      showBulkActionResult(this.newAlert, bundles, skippedIds, {
        success: (count) => `Archived ${count} ${pluralizeBundles(count)}.`,
        partialTitle: 'Some bundles were skipped',
        partial: (count, skipped) =>
          `Archived ${count} of ${count + skipped.length} bundles. Skipped: ${formatBundleNames(skipped)}.`,
      })
    } catch (e) {
      showBulkActionError(
        this.newAlert,
        e,
        'You do not have permission to archive bundles in this workspace.',
        'There was an error archiving the bundles.',
      )
      this.close()
      return
    }

    await this.refreshBundles(bundles[0].workspace)

    this.close()
  })
}
